import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "../../lib/api";
import DashboardLayout from "../../components/layout/DashboardLayout";
import StatusBadge from "../../components/StatusBadge";
import { Input } from "../../components/ui/input";
import { formatDateTime } from "../../lib/format";

const BTN = "text-xs font-mono uppercase tracking-[0.18em] border px-3 py-1 rounded-sm";

function parseUsbId(raw) {
    const [vid, pid, ...rest] = String(raw || "").trim().split(":");
    const serial = rest.join(":");
    if (!vid || !pid || !serial) return null;
    return { vid, pid, serial };
}

export default function AdminUsbKeys() {
    const [keys, setKeys] = useState([]);
    const [q, setQ] = useState("");
    const [form, setForm] = useState({ usbId: "", ownerEmail: "", label: "", notes: "" });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/usb-keys");
            setKeys(data || []);
        } catch (err) { toast.error(apiErrorMessage(err)); }
    };
    useEffect(() => { load(); }, []);

    const filtered = keys.filter((k) => {
        const s = q.trim().toLowerCase();
        return !s || k.usb_id.toLowerCase().includes(s) || (k.owner?.email || "").toLowerCase().includes(s)
            || (k.label || "").toLowerCase().includes(s) || (k.notes || "").toLowerCase().includes(s);
    });

    const register = async (e) => {
        e.preventDefault();
        const usb = parseUsbId(form.usbId);
        if (!usb) { toast.error("USB ID inválido. Formato: VID:PID:SERIAL (cópialo desde AVX Pro Central)"); return; }
        setSaving(true);
        try {
            await api.post("/admin/usb-keys", {
                usb: { ...usb, label: form.label.trim() || null },
                owner_email: form.ownerEmail.trim() || null,
                notes: form.notes.trim() || null,
            });
            toast.success("Llave USB registrada");
            setForm({ usbId: "", ownerEmail: "", label: "", notes: "" });
            await load();
        } catch (err) { toast.error(apiErrorMessage(err)); }
        finally { setSaving(false); }
    };

    const update = async (k, body, okMsg) => {
        try {
            const { data } = await api.patch(`/admin/usb-keys/${k.id}`, body);
            const freed = data.released_activations ? ` · ${data.released_activations} activación(es) liberada(s)` : "";
            toast.success(okMsg + freed);
            await load();
        } catch (err) { toast.error(apiErrorMessage(err)); }
    };

    const assign = (k) => {
        const v = window.prompt(
            "Email del cliente (vacío para quitar la asignación).\nSi cambia el dueño, se liberan las licencias que tenga la llave.",
            k.owner?.email || "",
        );
        if (v === null) return;
        update(k, { owner_email: v.trim() }, v.trim() ? "Llave asignada" : "Asignación quitada");
    };

    const toggle = (k) => {
        if (k.status === "active") {
            if (!window.confirm("¿Deshabilitar esta llave (perdida/robada)? Se liberan todas sus licencias y los plugins la bloquean al verificar online.")) return;
            update(k, { status: "disabled" }, "Llave deshabilitada");
        } else if (k.status === "blocked") {
            if (!window.confirm("¿Desbloquear esta llave? El cliente la formateó. Quedará atada a su formato actual en la próxima sincronización desde AVX Pro Central (lo ideal es reformatearla tú desde AVX Pro Central).")) return;
            update(k, { status: "active" }, "Llave desbloqueada");
        } else {
            update(k, { status: "active" }, "Llave habilitada");
        }
    };

    const rename = (k) => {
        const v = window.prompt("Nombre de la llave:", k.label || "AVX USB key");
        if (v === null) return;
        update(k, { label: v }, "Nombre actualizado");
    };

    const editNotes = (k) => {
        const v = window.prompt("Notas internas:", k.notes || "");
        if (v === null) return;
        update(k, { notes: v }, "Notas actualizadas");
    };

    const remove = async (k) => {
        if (!window.confirm("¿Eliminar del registro? El pendrive deja de ser una AVX USB key y se liberan sus licencias.")) return;
        try {
            await api.delete(`/admin/usb-keys/${k.id}`);
            toast.success("Llave eliminada del registro");
            await load();
        } catch (err) { toast.error(apiErrorMessage(err)); }
    };

    return (
        <DashboardLayout role="admin">
            <div className="mb-8 flex items-center justify-between flex-wrap gap-4" data-testid="admin-usb-keys-page">
                <div>
                    <div className="label-mono mb-3">/ Llaves USB</div>
                    <h1 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter">AVX USB keys</h1>
                    <p className="text-sm text-avx-muted mt-2 max-w-2xl">
                        Solo los pendrives registrados aquí pueden guardar licencias. Prepáralo con
                        “Format as AVX USB key” en AVX Pro Central (cuenta admin, pestaña USB Keys: deja solo una partición pequeña para
                        la licencia), regístralo ahí o pega su USB ID abajo. Puedes asignarlo tú a un cliente o dejarlo sin asignar: el primer cliente que lo
                        conecte en AVX Pro Central y confirme “Link to my account” queda como dueño.
                    </p>
                </div>
                <Input placeholder="Buscar USB ID, email, nombre…" value={q} onChange={(e) => setQ(e.target.value)} className="bg-avx-surface border-avx-line h-11 w-72" data-testid="usb-keys-search" />
            </div>

            <form onSubmit={register} className="panel-elevated p-5 mb-8 grid lg:grid-cols-[2fr_1.5fr_1fr_auto] gap-3 items-end" data-testid="usb-key-register-form">
                <div>
                    <div className="label-mono mb-2">USB ID</div>
                    <Input placeholder="0781:5581:4C530001230508116094" value={form.usbId} onChange={(e) => setForm({ ...form, usbId: e.target.value })} className="bg-avx-surface border-avx-line h-11 font-mono" required />
                </div>
                <div>
                    <div className="label-mono mb-2">Cliente (opcional)</div>
                    <Input type="email" placeholder="cliente@email.com" value={form.ownerEmail} onChange={(e) => setForm({ ...form, ownerEmail: e.target.value })} className="bg-avx-surface border-avx-line h-11" />
                </div>
                <div>
                    <div className="label-mono mb-2">Nombre (opcional)</div>
                    <Input placeholder="AVX USB key" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="bg-avx-surface border-avx-line h-11" />
                </div>
                <button type="submit" disabled={saving} className="btn-primary px-5 h-11 text-xs font-mono uppercase tracking-[0.2em] rounded-sm" data-testid="usb-key-register-btn">
                    {saving ? "Registrando…" : "Registrar"}
                </button>
            </form>

            <div className="space-y-3">
                {filtered.length === 0 ? (
                    <div className="panel p-10 text-center text-avx-muted">Sin llaves USB registradas.</div>
                ) : filtered.map((k) => (
                    <div key={k.id} className="panel-elevated p-5" data-testid={`admin-usb-key-${k.id}`}>
                        <div className="grid lg:grid-cols-[1fr_auto] gap-4 items-start">
                            <div>
                                <div className="flex items-center gap-3 flex-wrap mb-2">
                                    <span className="font-bold text-base">{k.label || "AVX USB key"}</span>
                                    <StatusBadge status={k.status === "active" || k.status === "blocked" ? k.status : "disabled"} />
                                    <span className="label-mono">{k.licenses.length} licencia{k.licenses.length === 1 ? "" : "s"}</span>
                                </div>
                                <div className="font-mono text-xs text-avx-muted break-all">{k.usb_id}</div>
                                <div className="text-sm text-avx-muted mt-1">
                                    {k.owner ? (
                                        <><span className="text-avx-text">{k.owner.name}</span> · <span className="font-mono text-xs">{k.owner.email}</span></>
                                    ) : (
                                        <span className="text-avx-blue">Sin asignar</span>
                                    )}
                                </div>
                                {k.notes && <div className="text-xs text-avx-muted mt-1">{k.notes}</div>}
                                <div className="font-mono text-[10px] text-avx-muted uppercase tracking-[0.18em] mt-1">
                                    Registrada {formatDateTime(k.created_at)}
                                    {k.assigned_at
                                        ? ` · ${k.assigned_via === "claim" ? "Vinculada por el cliente" : "Asignada"} ${formatDateTime(k.assigned_at)}`
                                        : ""}
                                    {k.blocked_at ? ` · Bloqueada ${formatDateTime(k.blocked_at)}` : ""}
                                </div>
                            </div>
                            <div className="flex gap-2 flex-wrap">
                                <button onClick={() => assign(k)} className={`${BTN} text-avx-blue hover:bg-avx-blue/10 border-avx-line`}>Asignar</button>
                                <button onClick={() => rename(k)} className={`${BTN} text-avx-muted hover:text-avx-text border-avx-line`}>Nombre</button>
                                <button onClick={() => editNotes(k)} className={`${BTN} text-avx-muted hover:text-avx-text border-avx-line`}>Notas</button>
                                {k.status === "active" ? (
                                    <button onClick={() => toggle(k)} className={`${BTN} text-avx-danger hover:bg-avx-danger/10 border-avx-danger/30`}>Deshabilitar</button>
                                ) : (
                                    <button onClick={() => toggle(k)} className={`${BTN} text-avx-success hover:bg-avx-success/10 border-avx-success/30`}>{k.status === "blocked" ? "Desbloquear" : "Habilitar"}</button>
                                )}
                                <button onClick={() => remove(k)} className={`${BTN} text-avx-muted hover:text-avx-danger border-avx-line`}>Eliminar</button>
                            </div>
                        </div>
                        {k.licenses.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-avx-line space-y-1">
                                {k.licenses.map((l) => (
                                    <div key={l.id} className="text-xs font-mono">
                                        {l.product_name || l.product_id} <span className="text-avx-muted">· {formatDateTime(l.activated_at)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </DashboardLayout>
    );
}
